import React, { useEffect, useState } from "react";
import { Card, ConfigProvider, Tag, Descriptions, Alert, message } from "antd";
import {
  FormButtonGroup,
  Submit,
  Checkbox,
  TimePicker,
} from "@formily/antd-v5";
import {
  createForm,
  setValidateLanguage,
  registerValidateLocale,
  onFieldValueChange,
} from "@formily/core";
import { FormProvider, createSchemaField } from "@formily/react";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import locale from "antd/locale/nl_NL";
import "dayjs/locale/nl";
import dayjs from "dayjs";

import {
  RumbleWelcome,
  RumbleDamageSideSelector,
  RumbleCompaniesSelect,
  RumbleServicePartnerSelector,
  RumbleDealershipSelector,
  RumbleLocationSelector,
  RumbleStepButtons,
  RumbleImageUpload,
  RumbleMerkzeker,
  LicensePlate,
  Address,
  NumberPicker,
  Email,
  Input,
  TextArea,
  Text,
  Html,
  Select,
  Switch,
  FormItem,
  Password,
  DatePicker,
  Button,
  Grid,
  Flex,
  Debug,
  RumbleActOfCessionExample,
  RumbleSignature,
  RumbleImageCapturePending,
  Telephone,
  RumbleFormGrid,
  RumbleCalendar,
  RumbleTimeSlotPicker,
  RumbleDealerIntakeHeader,
  RumbleFormDecorator,
} from "@rumble-it/common-components/components/Rumble";

import { resolveFormConfigurationTemplate } from "@rumble-it/common-lib/src/helpers/formConfiguration";

import { fetchData } from "../lib/api/fetchData";

import { uploadMedia } from "../lib/api/uploadMedia";

import { autorun } from "@formily/reactive";

import { Upload, Form, FormStep, FormGrid } from "@formily/antd-v5";

import * as AntdIcons from "@ant-design/icons";

import { createStore, StateMachineProvider } from "little-state-machine";
import { useMutation, useQuery } from "@tanstack/react-query";
import { LoadingState } from "../components/loading-state";
import { getCurrentUser } from "../lib/queries/getCurrentUser";
import { getCompanies } from "../lib/queries/getCompanies";
import { getFormConfigurationsByType } from "../lib/queries/getFormConfigurationsByType";
import { getFormConfiguration } from "../lib/queries/getFormConfiguration";
import { getAppointmentTimeSlotsByServicePartner } from "../lib/queries/getAppointmentTimeSlotsByServicePartner";
import { getCauseOfDamages } from "../lib/queries/getCauseOfDamages";
import { getInsuranceTypes } from "../lib/queries/getInsuranceTypes";
import { getDamageFromDirections } from "../lib/queries/getDamageFromDirections";
import { getDriverTypes } from "../lib/queries/getDriverTypes";
import { getPaymentByTypes } from "../lib/queries/getPaymentByTypes";
import { getReplacementVehicleTypes } from "../lib/queries/getReplacementVehicleTypes";
import { getTypeOfDamages } from "../lib/queries/getTypeOfDamages";
import {
  fetchReplacementTransportPlansByCompanyId,
  mapReplacementTransportPlansToSelectOptions,
} from "../../../lib/api/resources/replacementTransports";
import {
  createConcept,
  fetchConceptById,
} from "@/lib/api/resources/dossierConcepts";
import { useSearchParams } from "react-router-dom";

const form = createForm({
  effects: () => {
    onFieldValueChange("*", (field) => {
      if (field.value === "") {
        /* @ts-ignore */
        field.setValue(null);
      }
    });
  },
});

const formStep = FormStep.createFormStep();

const SchemaField = createSchemaField({
  components: {
    FormGrid,
    FormStep,
    FormItem,
    Email,
    Input,
    RumbleInput: Input,
    NumberPicker,
    Password,
    Select,
    RumbleSelect: Select,
    DatePicker,
    RumbleLicensePlate: LicensePlate,
    LicensePlate,
    RumbleWelcome,
    RumbleDamageSideSelector,
    RumbleDatePicker: DatePicker,
    RumbleCompaniesSelect,
    RumbleServicePartnerSelector,
    RumbleDealershipSelector,
    RumbleLocationSelector,
    RumbleMerkzeker,
    RumbleFormGrid,
    RumbleCalendar,
    RumbleTimeSlotPicker,
    Card,
    Html,
    RumbleHtml: Html,
    Upload,
    RumbleTextArea: TextArea,
    TextArea,
    RumbleText: Text,
    Text,
    Button,
    RumbleStepButtons,
    Address,
    RumbleAddress: Address,
    RumbleImageUpload,
    Tag,
    Descriptions,
    RumbleSwitch: Switch,
    Switch,
    Grid,
    RumbleGrid: Grid,
    Flex,
    RumbleFlex: Flex,
    Debug,
    RumbleActOfCessionExample,
    RumbleSignature,
    RumbleImageCapturePending,
    Telephone,
    Checkbox,
    TimePicker,
    RumbleDealerIntakeHeader,
    RumbleFormDecorator,
  },
});

setValidateLanguage("nl-NL");

registerValidateLocale({
  "nl-NL": {
    required: "Dit veld is verplicht",
    enum: "Dit veld moet een van de volgende waarden zijn: {values}",
    pattern: "Dit veld moet voldoen aan het patroon",
    email: "Dit veld moet een geldig e-mailadres zijn",
  },
});

type defaultFormSchemaKeys = "form" | "onSuccess"; // Default keys for form schema.

export interface IFormContainerProps extends Record<string, unknown> {
  formType: string; // formType is the type of form, e.g. "repair-direct-generic-dealer-intake".
  formSchemaKey: defaultFormSchemaKeys | string; // e.g. "onSuccess" for success state, or any other key for active form.
}

export const FormContainer = (props: IFormContainerProps) => {
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const STORAGE_KEY = `rumbledirect-form-${
    props.formType ? props.formType : "default"
  }-${props.formSchemaKey ? props.formSchemaKey : "form"}`;

  if (!props.formSchemaKey) {
    return (
      <LoadingState
        isPending={false}
        isError={true}
        errorMessage="Formulier kon niet worden geladen. Geen formSchemaKey opgegeven."
      />
    );
  }

  const userQuery = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const user = await getCurrentUser();

      // First assert that the user has a role with one or more service partner(s).
      const servicePartners = user.role.servicePartners || [];

      if (servicePartners.length === 0) {
        throw new Error(
          "De huidige gebruiker heeft geen service partners gekoppeld.",
        );
      }

      // Return the user object when everything is fine.
      return user;
    },
  });

  const currentUser = userQuery.data;
  const hasConceptPermission =
    currentUser?.permissions.includes("admin_concept_nav");

  useEffect(() => {
    if (currentUser) {
      const servicePartners: Record<string, unknown>[] =
        currentUser.role.servicePartners || [];

      const servicePartner =
        servicePartners.length === 1
          ? (servicePartners[0].iri as string)
          : undefined;

      form.setInitialValues({
        servicePartner,
      });
    }
  }, [currentUser]);

  const dealershipsQuery = useQuery({
    queryKey: ["dealerships"],
    queryFn: async () => {
      const companies = await getCompanies({
        pagination: false,
        type: "dealership",
      });

      return companies.member.map((company) => ({
        ...company,
        iri: company["@id"],
      }));
    },
  });

  const dealerships = dealershipsQuery.data;

  useEffect(() => {
    const currentUserDealership = currentUser?.role?.dealership;

    if (currentUserDealership) {
      const dealershipDetails = dealerships?.find((dealership) =>
        dealership["@id"]
          ? dealership["@id"] === currentUserDealership["@id"]
          : false,
      );
      if (dealershipDetails) {
        form.setValues({
          dealership: dealershipDetails,
          dealershipCompany: currentUser?.role?.dealership?.["@id"],
        });
      } else {
        form.setValues({
          dealership: currentUser?.role?.dealership,
          dealershipCompany: currentUser?.role?.dealership?.["@id"],
        });
      }
    }
  }, [currentUser, dealerships]);

  const conceptId = searchParams.get("conceptId");

  const conceptQuery = useQuery({
    queryKey: ["concept", conceptId],
    queryFn: async () => {
      if (!conceptId) {
        throw new Error("No conceptId provided");
      }
      return await fetchConceptById(conceptId as string);
    },
    enabled: !!conceptId,
  });

  useEffect(() => {
    if (conceptQuery.data) {
      localStorage.removeItem(STORAGE_KEY);
      form.setInitialValues({
        ...conceptQuery.data.data,
      });
    }
  }, [conceptQuery.data]);

  const schemasQuery = useQuery({
    queryKey: ["formConfigurations", props.formType],
    queryFn: async () => {
      const availableForms = await getFormConfigurationsByType(props.formType);
      if (!availableForms || availableForms.length === 0) {
        throw new Error(
          `No form configurations found for type "${props.formType}".`,
        );
      }
      if (availableForms.length !== 1) {
        throw new Error(
          `Expected exactly one form configuration for type "${props.formType}", but found ${availableForms.length}.`,
        );
      }
      return availableForms[0];
    },
  });

  const formId = schemasQuery.data?.id;

  const schemaQuery = useQuery({
    queryKey: ["formConfiguration", formId],
    queryFn: async () => getFormConfiguration(formId),
    enabled: !!formId,
  });

  const formConfiguration = schemaQuery.data?.formConfiguration;
  const etag = schemaQuery.data?.etag;

  const saveConceptMutation = useMutation({
    mutationFn: async (conceptData: Record<string, any>) => {
      delete conceptData.signatureClient;

      return createConcept({
        data: conceptData,
        dossierType: "repair_direct.generic",
        label: `Concept opgeslagen op ${new Date().toLocaleString()}`,
      });
    },
    onSuccess: (data) => {
      setSearchParams({
        conceptId: data.id,
      });
      message.success("Concept succesvol opgeslagen.");
    },
    onError: () => {
      message.error(
        "Er is een fout opgetreden bij het opslaan van het concept. Probeer het later opnieuw.",
      );
    },
  });

  if (userQuery.isPending || userQuery.isError) {
    return (
      <LoadingState
        isPending={userQuery.isPending}
        isError={userQuery.isError}
        errorMessage="De huidige gebruiker kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  if (dealershipsQuery.isPending || dealershipsQuery.isError) {
    return (
      <LoadingState
        isPending={dealershipsQuery.isPending}
        isError={dealershipsQuery.isError}
        errorMessage="De dealers konden niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  if (schemaQuery.isPending || schemasQuery.isError) {
    return (
      <LoadingState
        isPending={schemaQuery.isPending}
        isError={schemaQuery.isError}
        errorMessage="Het formulier kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed: {
        etag?: string | null;
        values?: Record<string, any>;
        currentStep?: number | null;
      } | null = JSON.parse(stored);
      // if the etag is equal as the etag we have stored, we can use this data
      if (parsed !== null && etag === parsed.etag) {
        // Check if the dealership in the stored values, i.e. values.dealership.id matches
        // the dealership from the currentuser scope field, if not, clear the vehicleHandInLocation
        if (
          parsed.values?.dealership?.id &&
          parsed.values?.dealership?.id !== currentUser?.role?.dealership?.id
        ) {
          console.info(
            "FormContainer: Clearing vehicleHandInLocation due to dealership mismatch.",
          );
          form.setValues({
            vehicleHandInLocation: null,
          });
        }

        form.setInitialValues(parsed.values ?? {});

        // Make sure step index is in valid range
        if (typeof parsed.currentStep === "number" && parsed.currentStep >= 0) {
          formStep.setCurrent(parsed.currentStep);
        }
      }
    } catch (e) {
      console.warn("Failed to load saved form state:", e);
    }
  }

  autorun(() => {
    /**
     * Save data to local storage - only if form is active (not in success state)
     */
    if (props.formSchemaKey !== "onSuccess") {
      const data = {
        values: form.values,
        currentStep: formStep.current,
        etag: etag,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  });

  const formSubmitFailed = async (_e) => {
    setSubmitErrors([
      "Kijk nog een keer of alle velden juist zijn ingevuld en probeer het formulier opnieuw te verzenden.",
    ]);
    setShowErrors(true);
  };

  const formSubmit = async () => {
    // Clear any previous errors
    setSubmitErrors([]);
    setShowErrors(false);

    for (const [_path, field] of Object.entries(form.fields)) {
      if (field.componentType === "RumbleSignature") {
        try {
          const blob = await fetch(field.value as string).then((res) =>
            res.blob(),
          );
          const file = new File(
            [blob],
            `${field.props.filename ?? field.props.name}.png`,
            {
              type: "image/png",
            },
          );

          const response = await uploadMedia({
            file,
            onSuccess: () => {},
            onError: () => {},
          });

          if (!response || !response["@id"]) {
            console.error(
              "form-container.tsx: Failed to upload signature:",
              response,
            );
          }

          form.setValuesIn(field.props.name, response["@id"]);
        } catch (err) {
          console.error("form-container.tsx: Failed to upload signature", err);
        }
      }
    }

    const formattedData = resolveFormConfigurationTemplate(
      formConfiguration.settings.format,
      form,
    );

    // Add source field
    formattedData.source = "dealer-intake";

    try {
      /**
       * Post data to the endpoint
       */
      const response = await fetchData({
        endpoint: formConfiguration.settings.endpoint,
        method: "POST",
        data: formattedData,
      });

      localStorage.removeItem(STORAGE_KEY);

      if (response?.data?.dossier?.adminSuccessUrl) {
        // Redirect to the admin success URL
        window.location.href = response.data.dossier.adminSuccessUrl;
        return;
      }

      scrollToElement({ element: "#rumbledirect-form" });
    } catch (error) {
      console.error("Failed to post form configuration:", error);

      // Handle submission errors
      const errors = [];

      // Check if the error response contains structured error data
      if (error.response?.data?.errors) {
        // API returned structured errors
        errors.push(
          ...error.response.data.errors.map((err) =>
            typeof err === "string" ? err : err.message || String(err),
          ),
        );
      } else if (error.response?.data?.message) {
        // API returned a single error message
        errors.push(error.response.data.message);
      } else if (error.message) {
        // Use the error message directly
        errors.push(error.message);
      } else {
        // Fallback for unknown error formats
        errors.push(
          "Er is een onbekende fout opgetreden bij het versturen van het formulier.",
        );
      }

      // Update the error state to display to the user
      setSubmitErrors(errors);
      setShowErrors(true);
    }
  };

  const allHandInlocations = [
    ...(currentUser?.role?.servicePartners ?? []),
    ...dealerships,
  ];

  /**
   * Propagate Little State Machine to the form
   */
  const scope = {
    getAppointmentTimeSlotsByServicePartner,
    getCauseOfDamages,
    getDamageFromDirections,
    getDriverTypes,
    getInsuranceTypes,
    getPaymentByTypes,
    getReplacementVehicleTypes,
    getTypeOfDamages,
    fetchReplacementTransportPlansByCompanyId,
    mapReplacementTransportPlansToSelectOptions,
    dayjs,
    availableServicePartners:
      currentUser?.role?.servicePartners?.map((servicepartner) => ({
        ...servicepartner,
        label: `${servicepartner.fullName}`,
        value: servicepartner.iri,
      })) || [],
    availableHandInLocations: allHandInlocations.map((location) => ({
      ...location,
      label: location.fullName,
      value: location.iri,
    })),
    fetchData: fetchData,
    formStep: formStep,
    formSubmit: formSubmit,
    uploadMedia: uploadMedia,
    icon: (iconName) => {
      // Dynamically get the icon component from @ant-design/icons
      const IconComponent = AntdIcons[iconName];
      if (IconComponent) {
        // If the icon exists, create and return the React element
        return React.createElement(IconComponent);
      }
      // Optionally, return null or a default icon if not found
      return null;
    },
    currentUser,
  };

  createStore({ scope: scope });

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <StateMachineProvider>
        <ConfigProvider
          theme={formConfiguration.settings.theme}
          locale={locale}
        >
          <div
            id="rumbledirect-form"
            style={{ width: "100%", maxWidth: "56rem" }}
          >
            <FormProvider form={form}>
              <Form layout={"vertical"}>
                <SchemaField
                  schema={formConfiguration[props.formSchemaKey]}
                  scope={scope}
                />

                {props.formSchemaKey !== "onSuccess" ? (
                  <FormButtonGroup>
                    {hasConceptPermission ? (
                      <Button
                        disabled={searchParams.get("conceptId") ? true : false}
                        type="default"
                        loading={saveConceptMutation.isPending}
                        onClick={() => saveConceptMutation.mutate(form.values)}
                      >
                        Opslaan als concept
                      </Button>
                    ) : null}
                    <Submit
                      type="primary"
                      onSubmit={formSubmit}
                      onSubmitFailed={formSubmitFailed}
                      style={{ width: "100%" }}
                    >
                      Versturen
                    </Submit>
                  </FormButtonGroup>
                ) : null}
              </Form>

              {showErrors && submitErrors.length > 0 ? (
                <Alert
                  message="Er zijn fouten opgetreden bij het versturen van het formulier"
                  description={
                    <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                      {submitErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  style={{ marginTop: "24px" }}
                  onClose={() => setShowErrors(false)}
                />
              ) : null}
            </FormProvider>
          </div>
        </ConfigProvider>
      </StateMachineProvider>
    </div>
  );
};

export default FormContainer;
